import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search,
  Refresh,
  OpenInNew,
  LocalShipping,
  CheckCircle,
  AssignmentReturn,
} from '@mui/icons-material';
import {
  VerticalTimeline,
  VerticalTimelineElement,
} from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { useApiClient } from '../utils/apiClient';

// Valid shipment statuses
const SHIPMENT_STATUSES = [
  'Registered',
  'Label Printed',
  'In Transit',
  'Out For Delivery',
  'Delivered',
  'Exception',
  'Return to Sender: In Transit',
  'Return to Sender: Exception',
  'Return to Sender: Out for Delivery',
  'Return to Sender: Delivered',
];

const TrackingWebhooks = () => {
  const apiClient = useApiClient();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  
  // Debug: Log selectedWebhook changes
  useEffect(() => {
    console.log('selectedWebhook changed:', selectedWebhook);
  }, [selectedWebhook]);
  const [filters, setFilters] = useState({
    shipiumTrackingId: '',
    shipiumShipmentId: '',
    carrierTrackingId: '',
    carrierId: '',
    shipmentStatus: '',
  });
  const [sortBy, setSortBy] = useState('carrierEstimatedDeliveryDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Use refs to track current values without causing re-renders
  const pageRef = useRef(page);
  const rowsPerPageRef = useRef(rowsPerPage);
  const filtersRef = useRef(filters);
  const sortByRef = useRef(sortBy);
  const sortOrderRef = useRef(sortOrder);

  // Update refs when values change
  useEffect(() => {
    pageRef.current = page;
  }, [page]);
  useEffect(() => {
    rowsPerPageRef.current = rowsPerPage;
  }, [rowsPerPage]);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);
  useEffect(() => {
    sortOrderRef.current = sortOrder;
  }, [sortOrder]);

  // Create a stable fetch function that doesn't depend on changing values
  const fetchWebhooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: (pageRef.current + 1).toString(),
        pageSize: rowsPerPageRef.current.toString(),
      });

      // Use current filters from ref
      const currentFilters = filtersRef.current;
      Object.keys(currentFilters).forEach((key) => {
        if (currentFilters[key]) {
          params.append(key, currentFilters[key]);
        }
      });

      // Add sorting parameters
      params.append('sortBy', sortByRef.current);
      params.append('sortOrder', sortOrderRef.current);

      const response = await apiClient.get(`/webhooks/tracking?${params.toString()}`);
      
      // Backend returns { data: [...], pagination: {...} }
      const webhooksData = response.data?.data || response.data || [];
      const paginationData = response.data?.pagination || response.pagination || {};
      
      setWebhooks(webhooksData);
      setTotalCount(paginationData.total || 0);
      
      // Automatically select the first row if data is available and no row is currently selected
      if (webhooksData.length > 0 && !selectedWebhook) {
        setSelectedWebhook(webhooksData[0]);
      }
    } catch (err) {
      console.error('Failed to fetch tracking webhooks:', err);
      
      // Build detailed error message
      let errorMessage = err.message || 'Failed to load tracking webhooks';
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = `404 Not Found: The /webhooks/tracking endpoint is not available. Please ensure the server has been restarted.`;
        } else if (err.response.status === 401) {
          errorMessage = `401 Unauthorized: Authentication failed. Please log in again.`;
        } else if (err.response.data?.message) {
          errorMessage = `${err.response.status}: ${err.response.data.message}`;
        } else {
          errorMessage = `${err.response.status} ${err.response.statusText}: ${errorMessage}`;
        }
      }
      
      setError(errorMessage);
      setWebhooks([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [apiClient]); // Only depend on apiClient, use refs for everything else

  // Create a stable key for filters to track changes
  const filtersKey = useMemo(() => 
    JSON.stringify(filters), 
    [filters.shipiumTrackingId, filters.shipiumShipmentId, filters.carrierTrackingId, filters.carrierId, filters.shipmentStatus]
  );

  // Create a stable key for sorting to track changes
  const sortKey = useMemo(() => 
    `${sortBy}-${sortOrder}`, 
    [sortBy, sortOrder]
  );

  // Track previous values to detect changes
  const prevPage = useRef(page);
  const prevRowsPerPage = useRef(rowsPerPage);
  const prevFiltersKey = useRef(filtersKey);
  const prevSortKey = useRef(sortKey);
  const isInitialMount = useRef(true);

  // Fetch on initial mount
  useEffect(() => {
    fetchWebhooks();
    isInitialMount.current = false;
    prevPage.current = page;
    prevRowsPerPage.current = rowsPerPage;
    prevFiltersKey.current = filtersKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Fetch when page or rowsPerPage changes
  useEffect(() => {
    if (!isInitialMount.current && (prevPage.current !== page || prevRowsPerPage.current !== rowsPerPage)) {
      prevPage.current = page;
      prevRowsPerPage.current = rowsPerPage;
      fetchWebhooks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  // Fetch when filters change (and reset to page 0)
  useEffect(() => {
    if (!isInitialMount.current && prevFiltersKey.current !== filtersKey) {
      prevFiltersKey.current = filtersKey;
      // Reset to page 0 if not already there
      if (pageRef.current !== 0) {
        setPage(0);
      } else {
        // If already on page 0, fetch directly
        fetchWebhooks();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Fetch when sorting changes (and reset to page 0)
  useEffect(() => {
    if (!isInitialMount.current && prevSortKey.current !== sortKey) {
      prevSortKey.current = sortKey;
      // Reset to page 0 if not already there
      if (pageRef.current !== 0) {
        setPage(0);
      } else {
        // If already on page 0, fetch directly
        fetchWebhooks();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(0); // Reset to first page when filter changes
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
    setPage(0); // Reset to first page when sort changes
  };

  const handleOpenTrackingLink = (link) => {
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const formatLocalDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    
    const statusLower = status.toLowerCase();
    const statusColors = {
      'delivered': 'success',
      'return to sender: delivered': 'success',
      'in transit': 'info',
      'return to sender: in transit': 'info',
      'out for delivery': 'info',
      'return to sender: out for delivery': 'info',
      'registered': 'default',
      'label printed': 'default',
      'exception': 'error',
      'return to sender: exception': 'error',
      'unknown': 'default',
    };
    return statusColors[statusLower] || 'default';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Shipment Tracking
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchWebhooks}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <TextField
            label="Shipium Tracking ID"
            value={filters.shipiumTrackingId}
            onChange={(e) => handleFilterChange('shipiumTrackingId', e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Shipium Shipment ID"
            value={filters.shipiumShipmentId}
            onChange={(e) => handleFilterChange('shipiumShipmentId', e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Carrier Tracking ID"
            value={filters.carrierTrackingId}
            onChange={(e) => handleFilterChange('carrierTrackingId', e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Carrier ID"
            value={filters.carrierId}
            onChange={(e) => handleFilterChange('carrierId', e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Shipment Status</InputLabel>
            <Select
              value={filters.shipmentStatus}
              onChange={(e) => handleFilterChange('shipmentStatus', e.target.value)}
              label="Shipment Status"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {SHIPMENT_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Error Alert - Persistent */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
          action={
            <Button color="inherit" size="small" onClick={fetchWebhooks}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error Loading Shipment Tracking</AlertTitle>
          <Typography variant="body2" component="div">
            {error}
          </Typography>
          {error.includes('404') && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              The endpoint may not be available. Please ensure the server has been restarted and the endpoint is registered.
            </Typography>
          )}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 600, overflowY: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'shipiumTrackingId'}
                        direction={sortBy === 'shipiumTrackingId' ? sortOrder : 'asc'}
                        onClick={() => handleSort('shipiumTrackingId')}
                      >
                        Shipium Tracking ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'shipiumShipmentId'}
                        direction={sortBy === 'shipiumShipmentId' ? sortOrder : 'asc'}
                        onClick={() => handleSort('shipiumShipmentId')}
                      >
                        Shipium Shipment ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Partner Shipment ID</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'carrierId'}
                        direction={sortBy === 'carrierId' ? sortOrder : 'asc'}
                        onClick={() => handleSort('carrierId')}
                      >
                        Carrier ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'carrierTrackingId'}
                        direction={sortBy === 'carrierTrackingId' ? sortOrder : 'asc'}
                        onClick={() => handleSort('carrierTrackingId')}
                      >
                        Carrier Tracking ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Carrier Service Method ID</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'shipmentStatus'}
                        direction={sortBy === 'shipmentStatus' ? sortOrder : 'asc'}
                        onClick={() => handleSort('shipmentStatus')}
                      >
                        Shipment Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'carrierEstimatedDeliveryDate'}
                        direction={sortBy === 'carrierEstimatedDeliveryDate' ? sortOrder : 'asc'}
                        onClick={() => handleSort('carrierEstimatedDeliveryDate')}
                      >
                        Est. Delivery Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'deliveredAtDateTime'}
                        direction={sortBy === 'deliveredAtDateTime' ? sortOrder : 'asc'}
                        onClick={() => handleSort('deliveredAtDateTime')}
                      >
                        Delivered Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'createdAt'}
                        direction={sortBy === 'createdAt' ? sortOrder : 'asc'}
                        onClick={() => handleSort('createdAt')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Events</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webhooks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No shipment tracking records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    webhooks.map((webhook) => (
                      <TableRow 
                        key={webhook.id} 
                        hover
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Row clicked:', webhook);
                          console.log('Setting selectedWebhook to:', webhook);
                          setSelectedWebhook(webhook);
                        }}
                        sx={{ 
                          cursor: 'pointer',
                          backgroundColor: selectedWebhook?.id === webhook.id ? 'action.selected' : 'inherit',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <TableCell>{webhook.shipiumTrackingId || 'N/A'}</TableCell>
                        <TableCell>{webhook.shipiumShipmentId || 'N/A'}</TableCell>
                        <TableCell>{webhook.partnerShipmentId || 'N/A'}</TableCell>
                        <TableCell>{webhook.carrierId || 'N/A'}</TableCell>
                        <TableCell>{webhook.carrierTrackingId || 'N/A'}</TableCell>
                        <TableCell>{webhook.carrierServiceMethodId || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={webhook.shipmentStatus || 'Unknown'}
                            color={getStatusColor(webhook.shipmentStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(webhook.carrierEstimatedDeliveryDate)}</TableCell>
                        <TableCell>{formatDate(webhook.deliveredAtDateTime)}</TableCell>
                        <TableCell>{formatDate(webhook.createdAt)}</TableCell>
                        <TableCell align="right">
                          {webhook.trackingEvents && Array.isArray(webhook.trackingEvents) 
                            ? webhook.trackingEvents.length 
                            : 0}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenTrackingLink(webhook.carrierTrackingLink)}
                            disabled={!webhook.carrierTrackingLink}
                            title={webhook.carrierTrackingLink ? 'Open tracking link' : 'No tracking link available'}
                          >
                            <OpenInNew />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Detail Panel */}
      {selectedWebhook && (
        <Paper sx={{ mt: 3, p: 3, border: '2px solid', borderColor: 'primary.main' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Tracking Details - {selectedWebhook.shipiumTrackingId || 'N/A'}
          </Typography>
          
          <Grid container spacing={3}>
            {/* Left Side - Column Data */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom>
                    Shipment Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Shipium Tenant ID</Typography>
                      <Typography variant="body1">{selectedWebhook.shipiumTenantId || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Partner Tenant ID</Typography>
                      <Typography variant="body1">{selectedWebhook.partnerTenantId || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Carrier ID</Typography>
                      <Typography variant="body1">{selectedWebhook.carrierId || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Carrier Tracking ID</Typography>
                      <Typography variant="body1">{selectedWebhook.carrierTrackingId || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Carrier Service Method ID</Typography>
                      <Typography variant="body1">{selectedWebhook.carrierServiceMethodId || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Shipment Status</Typography>
                      <Chip
                        label={selectedWebhook.shipmentStatus || 'Unknown'}
                        color={getStatusColor(selectedWebhook.shipmentStatus)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Shipped Date/Time</Typography>
                      <Typography variant="body1">{formatDate(selectedWebhook.shippedDateTime)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Delivered Date/Time</Typography>
                      <Typography variant="body1">{formatDate(selectedWebhook.deliveredAtDateTime)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Carrier Estimated Delivery Date</Typography>
                      <Typography variant="body1">{formatDate(selectedWebhook.carrierEstimatedDeliveryDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Original Carrier Estimated Delivery Date</Typography>
                      <Typography variant="body1">{formatDate(selectedWebhook.originalCarrierEstimatedDeliveryDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Partner Reference ID</Typography>
                      <Typography variant="body1">{selectedWebhook.partnerReferenceId || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Partner Reference ID 2</Typography>
                      <Typography variant="body1">{selectedWebhook.partnerReferenceId2 || 'N/A'}</Typography>
                    </Box>
                    {selectedWebhook.carrierTrackingLink && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Carrier Tracking Link</Typography>
                        <Button
                          size="small"
                          startIcon={<OpenInNew />}
                          onClick={() => handleOpenTrackingLink(selectedWebhook.carrierTrackingLink)}
                          sx={{ mt: 0.5 }}
                        >
                          Open Tracking
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Side - Timeline */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom>
                    Tracking Events Timeline
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {selectedWebhook.trackingEvents && Array.isArray(selectedWebhook.trackingEvents) && selectedWebhook.trackingEvents.length > 0 ? (
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, height: '100%' }}>
                      <Box
                        sx={{
                          '& .vertical-timeline': {
                            paddingTop: '20px !important',
                            paddingBottom: '20px !important',
                            '&::before': {
                              top: '20px !important',
                              bottom: '20px !important',
                            },
                          },
                          '& .vertical-timeline-element': {
                            '&:first-of-type': {
                              marginTop: 0,
                              '& .vertical-timeline-element-icon': {
                                top: '20px !important',
                              },
                            },
                            '&:last-of-type': {
                              marginBottom: 0,
                            },
                          },
                        }}
                      >
                        <VerticalTimeline>
                          {(() => {
                            const sortedEvents = [...selectedWebhook.trackingEvents].sort((a, b) => {
                              const dateA = a.eventDate ? new Date(a.eventDate) : new Date(0);
                              const dateB = b.eventDate ? new Date(b.eventDate) : new Date(0);
                              return dateB - dateA; // Most recent first
                            });
                            
                            return sortedEvents.map((event, index) => {
                          const locationParts = [];
                          if (event.city) locationParts.push(event.city);
                          if (event.region) locationParts.push(event.region);
                          if (event.postalCode) locationParts.push(event.postalCode);
                          if (event.country) locationParts.push(event.country);
                          const location = locationParts.join(', ');
                          
                          // Determine color based on shipment status
                          const status = event.shipmentStatus?.toLowerCase() || '';
                          let boxColor = '#1976d2'; // Default blue
                          let iconColor = '#1976d2';
                          
                          if (status === 'exception') {
                            boxColor = '#d32f2f'; // Red
                            iconColor = '#d32f2f';
                          } else if (status === 'delivered') {
                            boxColor = '#2e7d32'; // Green
                            iconColor = '#2e7d32';
                          } else if (status.includes('return to sender')) {
                            boxColor = '#f57c00'; // Yellow/Orange
                            iconColor = '#f57c00';
                          }
                          
                          // Determine if this is first or last event
                          const isFirst = index === 0;
                          const isLast = index === sortedEvents.length - 1;
                          const isLabelPrinted = status === 'label printed';
                          const isDelivered = status === 'delivered';
                          const isReturnDelivered = status === 'return to sender: delivered';
                          
                          // Choose icon based on position and status
                          let iconElement = null;
                          if (isFirst && isLabelPrinted) {
                            // First event (Label Printed) - use shipping icon
                            iconElement = <LocalShipping sx={{ fontSize: '1.5rem', color: '#fff' }} />;
                          } else if (isLast && (isDelivered || isReturnDelivered)) {
                            // Last event (Delivered) - use checkmark or return icon
                            if (isReturnDelivered) {
                              iconElement = <AssignmentReturn sx={{ fontSize: '1.5rem', color: '#fff' }} />;
                            } else {
                              iconElement = <CheckCircle sx={{ fontSize: '1.5rem', color: '#fff' }} />;
                            }
                          } else {
                            // Regular events - use colored circle
                            iconElement = <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', background: iconColor }} />;
                          }
                          
                          return (
                            <VerticalTimelineElement
                              key={index}
                              className="vertical-timeline-element--work"
                              contentStyle={{ 
                                background: boxColor, 
                                color: '#fff',
                                boxShadow: `0 3px 0 0 ${boxColor}`,
                                borderRadius: '4px',
                              }}
                              contentArrowStyle={{ 
                                borderRight: `7px solid ${boxColor}` 
                              }}
                              date={formatLocalDate(event.eventDate)}
                              dateClassName="vertical-timeline-element-date"
                              iconStyle={{ 
                                background: iconColor,
                                color: '#fff',
                                boxShadow: `0 0 0 4px ${iconColor}, inset 0 2px 0 rgba(0,0,0,0.08), 0 3px 0 4px rgba(0,0,0,0.05)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              icon={iconElement}
                            >
                              <Typography variant="h6" component="h3" sx={{ color: '#fff', mb: 1.5, fontWeight: 'bold' }}>
                                {event.carrierDescription || 'Event'}
                              </Typography>
                              {location && (
                                <Typography variant="body2" sx={{ color: '#fff', mb: 1, opacity: 0.9 }}>
                                  {location}
                                </Typography>
                              )}
                              {event.shipmentStatus && (
                                <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9, fontWeight: 'medium' }}>
                                  Status: {event.shipmentStatus}
                                </Typography>
                              )}
                            </VerticalTimelineElement>
                            );
                          });
                          })()}
                        </VerticalTimeline>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No tracking events available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Snackbar for transient notifications */}
      <Snackbar
        open={!!error && !error.includes('404')}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error}
      />
    </Box>
  );
};

export default TrackingWebhooks;

